import { useEffect, useRef, useState } from 'preact/hooks';
import { useOddball } from '../midi-context';
import type { SynthMode } from '../midi-context';
import { getAudioContext, createReverbBuffer } from '../audio/audio-engine';
import { playSoundfontNote, freqToMidi, isInstrumentLoaded, getCurrentProgram } from '../audio/soundfont-player';
import type { RollStartEvent, RollingEvent, RollDirectionChangeEvent, RollEndEvent, RollDiagnostics, CCUpdateEvent } from '../../src/types';
import { OddballCC } from '../../src/types';

// Scales: root-position modes that sound pleasant as generative melody
const SCALES: { name: string; freqs: number[] }[] = [
  { name: 'C pent',   freqs: [261.63, 293.66, 329.63, 392.00, 440.00, 523.25, 587.33, 659.25] },
  { name: 'D dorian', freqs: [293.66, 329.63, 349.23, 392.00, 440.00, 493.88, 523.25, 587.33] },
  { name: 'A blues',  freqs: [220.00, 261.63, 293.66, 311.13, 329.63, 392.00, 440.00, 523.25] },
  { name: 'E minor',  freqs: [164.81, 196.00, 220.00, 246.94, 293.66, 329.63, 349.23, 392.00] },
  { name: 'G mixo',   freqs: [196.00, 220.00, 246.94, 293.66, 329.63, 349.23, 392.00, 440.00] },
];

const HIST_LEN = 60;

interface MelodyEngine {
  ctx: AudioContext;
  masterGain: GainNode;
  reverb: ConvolverNode;
  wetGain: GainNode;
  active: boolean;
  scaleIndex: number;
  notePos: number;
  tempo: number;
  brightness: number;
  noteDuration: number;
  timer: number;
  noteCount: number;
  generatorId: string;
  genState: any;
  oscFreq: number;
  octaveShift: number;
  speedMul: number;
}

// ── Melody generators ────────────────────────────────────────────────

interface GeneratorDef {
  id: string;
  name: string;
  desc: string;
  pick: (eng: MelodyEngine, scaleLen: number) => void;
}

function buildPhrase(startPos: number, scaleLen: number): number[] {
  const clamp = (n: number) => Math.max(0, Math.min(scaleLen - 1, n));
  const shape = Math.floor(Math.random() * 4);
  const phrase: number[] = [];
  let pos = startPos;

  switch (shape) {
    case 0: // Arch: rise then fall
      for (let i = 0; i < 4; i++) { pos = clamp(pos + 1); phrase.push(pos); }
      for (let i = 0; i < 4; i++) { pos = clamp(pos - 1); phrase.push(pos); }
      break;
    case 1: // Valley: fall then rise
      for (let i = 0; i < 4; i++) { pos = clamp(pos - 1); phrase.push(pos); }
      for (let i = 0; i < 4; i++) { pos = clamp(pos + 1); phrase.push(pos); }
      break;
    case 2: // Ascending zigzag: up 2, back 1
      for (let i = 0; i < 8; i++) {
        pos = clamp(pos + (i % 2 === 0 ? 2 : -1));
        phrase.push(pos);
      }
      break;
    case 3: // Neighbor groups: center, upper, center, lower
      for (let i = 0; i < 2; i++) {
        phrase.push(pos);
        phrase.push(clamp(pos + 1));
        phrase.push(pos);
        phrase.push(clamp(pos - 1));
        pos = clamp(pos + 1);
      }
      break;
  }

  return phrase;
}

const GENERATORS: GeneratorDef[] = [
  {
    id: 'wander',
    name: 'Wander',
    desc: 'Random walk',
    pick(eng, scaleLen) {
      const steps = [-2, -1, -1, 0, 1, 1, 2];
      let step = steps[Math.floor(Math.random() * steps.length)];
      if (eng.notePos <= 1) step = Math.abs(step);
      else if (eng.notePos >= scaleLen - 2) step = -Math.abs(step);
      eng.notePos = Math.max(0, Math.min(scaleLen - 1, eng.notePos + step));
    },
  },
  {
    id: 'motif',
    name: 'Motif',
    desc: 'Repeating cells that transpose',
    pick(eng, scaleLen) {
      const s = eng.genState;
      const clamp = (n: number) => Math.max(0, Math.min(scaleLen - 1, n));

      if (!s.motif || s.pos >= s.motif.length) {
        if (s.motif && s.rep < s.maxRep) {
          // Repeat transposed
          const shift = [-1, 1, 2][Math.floor(Math.random() * 3)];
          s.motif = s.motif.map((n: number) => clamp(n + shift));
          s.pos = 0;
          s.rep++;
        } else {
          // Generate new motif
          const len = 3 + Math.floor(Math.random() * 3);
          const m = [eng.notePos];
          for (let i = 1; i < len; i++) {
            const choices = [-2, -1, 1, 2];
            m.push(clamp(m[i - 1] + choices[Math.floor(Math.random() * choices.length)]));
          }
          s.motif = m;
          s.pos = 0;
          s.rep = 0;
          s.maxRep = 2 + Math.floor(Math.random() * 2);
        }
      }

      eng.notePos = s.motif[s.pos++];
    },
  },
  {
    id: 'arcs',
    name: 'Arcs',
    desc: 'Shaped phrases with variations',
    pick(eng, scaleLen) {
      const s = eng.genState;
      const clamp = (n: number) => Math.max(0, Math.min(scaleLen - 1, n));

      if (!s.phrase || s.pos >= s.phrase.length) {
        if (s.phrase && !s.varied) {
          // Replay with small variations
          s.phrase = s.phrase.map((n: number, i: number) =>
            i > 0 && Math.random() < 0.3 ? clamp(n + (Math.random() < 0.5 ? 1 : -1)) : n
          );
          s.pos = 0;
          s.varied = true;
        } else {
          s.phrase = buildPhrase(eng.notePos, scaleLen);
          s.pos = 0;
          s.varied = false;
        }
      }

      eng.notePos = s.phrase[s.pos++];
    },
  },
  {
    id: 'velocity',
    name: 'Velocity',
    desc: 'Speed maps to pitch — slow=low, fast=high (3 octaves)',
    pick(eng, scaleLen) {
      const s = eng.genState;
      // 3-octave span: 1 below + base + 1 above, offset so 0 = bottom of lower octave
      const extLen = scaleLen * 3;
      const lo = -scaleLen;
      const hi = scaleLen * 2 - 1;
      const clamp = (n: number) => Math.max(lo, Math.min(hi, n));

      // Map oscillation frequency (0–5 Hz) across the full extended range
      const target = Math.round(Math.min(eng.oscFreq / 4, 1) * (extLen - 1)) + lo;

      if (s.lastTarget === undefined) {
        s.lastTarget = target;
        s.ornament = 0;
      }

      // Smooth motion toward target with musical steps
      const diff = target - eng.notePos;
      if (Math.abs(diff) <= 1 && Math.random() < 0.35) {
        // Near target — add neighbor-tone ornament
        s.ornament = (s.ornament + 1) % 3;
        const offsets = [0, 1, -1];
        eng.notePos = clamp(target + offsets[s.ornament]);
      } else if (diff !== 0) {
        // Move toward target, 1–3 steps (wider leaps for extended range)
        const absDiff = Math.abs(diff);
        const maxStep = absDiff > 8 ? 3 : absDiff > 3 ? 2 : 1;
        const step = diff > 0
          ? Math.min(diff, Math.random() < 0.3 ? maxStep : 1)
          : Math.max(diff, Math.random() < 0.3 ? -maxStep : -1);
        eng.notePos = clamp(eng.notePos + step);
      }

      s.lastTarget = target;
    },
  },
];

function createMelodyEngine(): MelodyEngine {
  const ctx = getAudioContext();
  const masterGain = ctx.createGain();
  masterGain.gain.value = 0;

  const reverb = ctx.createConvolver();
  reverb.buffer = createReverbBuffer(ctx, 2.5, 2);
  const wetGain = ctx.createGain();
  wetGain.gain.value = 0.35;
  const dryGain = ctx.createGain();
  dryGain.gain.value = 0.65;

  masterGain.connect(dryGain).connect(ctx.destination);
  masterGain.connect(reverb).connect(wetGain).connect(ctx.destination);

  return {
    ctx, masterGain, reverb, wetGain,
    active: false, scaleIndex: 0, notePos: 3,
    tempo: 250, brightness: 0.5, noteDuration: 0.6,
    timer: 0, noteCount: 0,
    generatorId: 'velocity', genState: {},
    oscFreq: 0, octaveShift: 0, speedMul: 1,
  };
}

function playNote(eng: MelodyEngine, synthMode: SynthMode): number {
  const { ctx, masterGain } = eng;
  const scale = SCALES[eng.scaleIndex % SCALES.length].freqs;
  const t = ctx.currentTime;

  const gen = GENERATORS.find(g => g.id === eng.generatorId) || GENERATORS[0];
  gen.pick(eng, scale.length);

  // notePos can exceed scale.length for extended-range generators — wrap into octaves
  const octave = Math.floor(eng.notePos / scale.length);
  const degree = ((eng.notePos % scale.length) + scale.length) % scale.length;
  const freq = scale[degree] * Math.pow(2, octave + eng.octaveShift);
  const dur = (eng.tempo / 1000) * eng.noteDuration;

  if (synthMode === 'soundfont' && isInstrumentLoaded(getCurrentProgram())) {
    playSoundfontNote(masterGain, freqToMidi(freq), dur * 1.8, 0.18);
  } else {
    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.value = freq;

    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 800 + eng.brightness * 4000;
    filter.Q.value = 1.5;

    const g = ctx.createGain();
    const vol = 0.18;
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(vol, t + 0.008);
    g.gain.setValueAtTime(vol, t + Math.max(0.01, dur - 0.05));
    g.gain.exponentialRampToValueAtTime(0.001, t + dur + 0.1);

    osc.connect(filter).connect(g).connect(masterGain);
    osc.start(t);
    osc.stop(t + dur + 0.15);
  }

  eng.noteCount++;
  return eng.notePos;
}

function startMelody(eng: MelodyEngine, synthModeRef: { current: SynthMode }) {
  if (eng.active) return;
  eng.active = true;
  eng.notePos = 3;
  eng.genState = {};
  eng.noteCount = 0;
  const t = eng.ctx.currentTime;
  eng.masterGain.gain.setTargetAtTime(0.7, t, 0.2);
  // More reverb for soundfont — samples benefit from extra space
  const wet = synthModeRef.current === 'soundfont' ? 0.5 : 0.35;
  eng.wetGain.gain.setTargetAtTime(wet, t, 0.1);
  scheduleNext(eng, synthModeRef);
}

function scheduleNext(eng: MelodyEngine, synthModeRef: { current: SynthMode }) {
  if (!eng.active) return;
  playNote(eng, synthModeRef.current);
  eng.timer = window.setTimeout(() => scheduleNext(eng, synthModeRef), eng.tempo / eng.speedMul);
}

function stopMelody(eng: MelodyEngine) {
  if (!eng.active) return;
  eng.active = false;
  clearTimeout(eng.timer);
  eng.timer = 0;
  const t = eng.ctx.currentTime;
  eng.masterGain.gain.setTargetAtTime(0, t, 0.4);
}

function changeScale(eng: MelodyEngine) {
  eng.scaleIndex++;
  const scale = SCALES[eng.scaleIndex % SCALES.length].freqs;
  eng.notePos = Math.min(eng.notePos, scale.length - 1);
}

// ── Diagnostics visualization helpers ───────────────────────────────

function MiniSparkline({ data, max, color, thresholdY, width = 140, height = 28 }: {
  data: number[];
  max: number;
  color: string;
  thresholdY?: number;
  width?: number;
  height?: number;
}) {
  if (data.length < 2) return <div style={{ width, height, background: '#111', borderRadius: 3 }} />;
  const clampMax = max || 1;
  const points = data.map((v, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - (Math.min(v, clampMax) / clampMax) * (height - 2) - 1;
    return `${x},${y}`;
  }).join(' ');

  let threshLine: preact.VNode | null = null;
  if (thresholdY !== undefined && thresholdY <= clampMax) {
    const ty = height - (thresholdY / clampMax) * (height - 2) - 1;
    threshLine = <line x1={0} y1={ty} x2={width} y2={ty} stroke="#555" strokeWidth="1" strokeDasharray="3,3" />;
  }

  return (
    <svg width={width} height={height} style={{ display: 'block', background: '#111', borderRadius: 3 }}>
      {threshLine}
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" />
    </svg>
  );
}

function Meter({ value, max, threshold, color, label, unit }: {
  value: number;
  max: number;
  threshold?: number;
  color: string;
  label: string;
  unit?: string;
}) {
  const pct = Math.min(100, (value / (max || 1)) * 100);
  const threshPct = threshold !== undefined ? Math.min(100, (threshold / (max || 1)) * 100) : undefined;
  const over = threshold !== undefined && value >= threshold;

  return (
    <div style={{ marginBottom: 4 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.6rem', color: '#666', marginBottom: 1 }}>
        <span>{label}</span>
        <span style={{ fontFamily: 'monospace', color: over ? color : '#888' }}>
          {value.toFixed(1)}{unit || ''}
          {threshold !== undefined && <span style={{ color: '#555' }}> / {threshold.toFixed(1)}</span>}
        </span>
      </div>
      <div style={{ position: 'relative', height: 6, background: '#1a1a1a', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{
          position: 'absolute', left: 0, top: 0, height: '100%',
          width: `${pct}%`,
          background: over ? color : '#333',
          borderRadius: 3,
          transition: 'width 0.1s, background 0.1s',
        }} />
        {threshPct !== undefined && (
          <div style={{
            position: 'absolute', top: 0, height: '100%',
            left: `${threshPct}%`,
            width: 1,
            background: '#666',
          }} />
        )}
      </div>
    </div>
  );
}

function StateTag({ label, active, color }: { label: string; active: boolean; color: string }) {
  return (
    <span style={{
      display: 'inline-block',
      padding: '1px 6px',
      borderRadius: 4,
      fontSize: '0.6rem',
      fontFamily: 'monospace',
      background: active ? color : '#1a1a1a',
      color: active ? '#fff' : '#444',
      border: `1px solid ${active ? color : '#333'}`,
    }}>
      {label}
    </span>
  );
}

// ── Main component ──────────────────────────────────────────────────

export function RollMelody() {
  const { parser, rollDetector, connected, speedModifier, octaveOffset, synthMode } = useOddball();
  const engineRef = useRef<MelodyEngine | null>(null);
  const [rolling, setRolling] = useState(false);
  const [scaleName, setScaleName] = useState(SCALES[0].name);
  const [noteCount, setNoteCount] = useState(0);
  const [generatorId, setGeneratorId] = useState('velocity');
  const [hue, setHue] = useState(180);
  const [tempo, setTempo] = useState(250);

  // Diagnostics state
  const [diag, setDiag] = useState<RollDiagnostics | null>(null);
  const energyFastHistRef = useRef<number[]>([]);
  const energySlowHistRef = useRef<number[]>([]);
  const omegaFastHistRef = useRef<number[]>([]);
  const omegaSlowHistRef = useRef<number[]>([]);
  const spinHistRef = useRef<number[]>([]);
  const freqHistRef = useRef<number[]>([]);
  const [histTick, setHistTick] = useState(0);
  const speedRef = useRef(1.0);
  const octaveRef = useRef(0);
  const synthModeRef = useRef<SynthMode>('oscillator');
  useEffect(() => { speedRef.current = speedModifier; }, [speedModifier]);
  useEffect(() => { octaveRef.current = octaveOffset; }, [octaveOffset]);
  useEffect(() => { synthModeRef.current = synthMode; }, [synthMode]);
  useEffect(() => {
    if (!connected) {
      const eng = engineRef.current;
      if (eng) stopMelody(eng);
      setRolling(false);
    }
  }, [connected]);

  useEffect(() => {
    const eng = createMelodyEngine();
    engineRef.current = eng;

    const syncModifiers = () => {
      eng.octaveShift = octaveRef.current;
      eng.speedMul = speedRef.current;
    };

    const onRollStart = (_evt: RollStartEvent) => {
      startMelody(eng, synthModeRef);
      setRolling(true);
      setNoteCount(0);
    };

    const onRolling = (e: RollingEvent) => {
      syncModifiers();
      eng.oscFreq = e.oscillationFreq;
      eng.tempo = Math.max(100, Math.min(400, 350 - e.oscillationFreq * 60));
      setTempo(Math.round(eng.tempo));
      eng.brightness = e.orientation.y / 127;
      eng.noteDuration = 0.3 + (e.spinSpeed / 127) * 0.5;
      setHue((e.orientation.x / 127) * 360);
      setNoteCount(eng.noteCount);
    };

    const onDirectionChange = (_evt: RollDirectionChangeEvent) => {
      changeScale(eng);
      setScaleName(SCALES[eng.scaleIndex % SCALES.length].name);
    };

    const onRollEnd = (_evt: RollEndEvent) => {
      stopMelody(eng);
      setRolling(false);
      setNoteCount(eng.noteCount);
    };

    // Poll diagnostics on every orient CC update (z axis = last in batch)
    const onCC = (evt: CCUpdateEvent) => {
      if (evt.cc !== OddballCC.OrientZ) return;
      const d = rollDetector.getDiagnostics();
      setDiag(d);

      // Push to history ring buffers
      const push = (arr: number[], v: number) => {
        arr.push(v);
        if (arr.length > HIST_LEN) arr.shift();
      };
      push(energyFastHistRef.current, d.energyFast);
      push(energySlowHistRef.current, d.energySlow);
      push(omegaFastHistRef.current, d.omegaFast);
      push(omegaSlowHistRef.current, d.omegaSlow);
      push(spinHistRef.current, d.spinEma);
      push(freqHistRef.current, d.oscillationFreq);
      setHistTick(n => n + 1);
    };

    rollDetector.on('rollStart', onRollStart);
    rollDetector.on('rolling', onRolling);
    rollDetector.on('rollDirectionChange', onDirectionChange);
    rollDetector.on('rollEnd', onRollEnd);
    parser.on('cc', onCC);

    // If already rolling when we mount, start immediately
    if (rollDetector.getDiagnostics().rolling) {
      startMelody(eng, synthModeRef);
      setRolling(true);
    }

    return () => {
      rollDetector.off('rollStart', onRollStart);
      rollDetector.off('rolling', onRolling);
      rollDetector.off('rollDirectionChange', onDirectionChange);
      rollDetector.off('rollEnd', onRollEnd);
      parser.off('cc', onCC);
      stopMelody(eng);
      eng.masterGain.disconnect();
    };
  }, [parser, rollDetector]);

  const d = diag;
  const th = d?.thresholds;

  // Determine which start path is closest to firing
  const fastProg = d && th ? d.fastHoldMs / th.fastStartHoldMs : 0;
  const slowProg = d && th ? d.slowHoldMs / th.slowStartHoldMs : 0;
  const endProg = d && th ? d.dropTimerMs / th.endHoldMs : 0;

  return (
    <div style={{ display: 'flex', gap: 24, minHeight: '100%', padding: '16px 0' }}>
      {/* Left: melody display */}
      <div
        style={{
          flex: '0 0 240px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '20px',
          background: rolling
            ? `radial-gradient(ellipse at center, hsla(${hue}, 40%, 18%, 0.6), transparent)`
            : 'transparent',
          borderRadius: '12px',
          padding: '24px 16px',
          transition: 'background 0.5s',
        }}
      >
        <h2 style={{ fontSize: '1rem', fontWeight: 300, color: '#888', margin: 0 }}>Roll Melody</h2>

        <div style={{ display: 'flex', gap: 2, borderRadius: 10, border: '1px solid #333', padding: 2 }}>
          {GENERATORS.map(g => (
            <button
              key={g.id}
              onClick={() => {
                const eng = engineRef.current;
                if (eng) { eng.generatorId = g.id; eng.genState = {}; }
                setGeneratorId(g.id);
              }}
              title={g.desc}
              style={{
                padding: '3px 10px',
                border: 'none',
                borderRadius: 8,
                background: generatorId === g.id ? '#333' : 'transparent',
                color: generatorId === g.id ? '#fff' : '#666',
                cursor: 'pointer',
                fontSize: '0.7rem',
                fontWeight: generatorId === g.id ? 600 : 400,
              }}
            >
              {g.name}
            </button>
          ))}
        </div>

        <div style={{
          fontSize: '1.4rem', fontWeight: 300,
          color: rolling ? `hsl(${hue}, 50%, 65%)` : '#333',
          letterSpacing: '0.1em', transition: 'color 0.3s',
        }}>
          {rolling ? 'Playing...' : 'Waiting for roll'}
        </div>

        {rolling && (
          <div style={{ fontSize: '0.8rem', color: '#666', fontFamily: 'monospace', textAlign: 'center' }}>
            {scaleName} &middot; {tempo}ms &middot; {noteCount} notes
          </div>
        )}

        <div style={{ fontSize: '0.65rem', color: '#444', textAlign: 'center', lineHeight: 1.6 }}>
          Roll = melody &middot; Speed = tempo
          <br />
          Direction change = new scale
        </div>
      </div>

      {/* Right: diagnostics panel */}
      <div style={{
        flex: 1,
        background: '#0d0d0d',
        borderRadius: 8,
        padding: '12px 16px',
        fontSize: '0.7rem',
        overflow: 'auto',
        minWidth: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <span style={{ color: '#666', fontWeight: 600, fontSize: '0.75rem' }}>Roll Detection State</span>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            <StateTag label="rolling" active={!!d?.rolling} color="#4a7" />
            <StateTag label="backoff" active={!!d?.backoff} color="#a44" />
            <StateTag label="impact-grace" active={!!d?.impactGraceActive} color="#a74" />
          </div>
        </div>

        {!d ? (
          <div style={{ color: '#444', padding: '20px 0', textAlign: 'center' }}>
            No data yet — connect the ball and move it
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            {/* Column 1: Energy meters + sparklines */}
            <div style={{ flex: '1 1 180px', minWidth: 160 }}>
              <div style={{ color: '#555', fontSize: '0.6rem', fontWeight: 600, marginBottom: 6 }}>ENERGY</div>
              <Meter
                label="E fast"
                value={d.energyFast}
                max={Math.max(50, th!.fastStartEnergy * 1.5)}
                threshold={th!.fastStartEnergy}
                color="#e85"
                unit=""
              />
              <Meter
                label="E slow"
                value={d.energySlow}
                max={Math.max(10, th!.slowStartEnergy * 4)}
                threshold={th!.slowStartEnergy}
                color="#5ae"
                unit=""
              />
              <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                <div>
                  <div style={{ fontSize: '0.55rem', color: '#555', marginBottom: 2 }}>E fast</div>
                  <MiniSparkline
                    data={energyFastHistRef.current}
                    max={Math.max(50, th!.fastStartEnergy * 1.5)}
                    color="#e85"
                    thresholdY={th!.fastStartEnergy}
                    width={120}
                    height={24}
                  />
                </div>
                <div>
                  <div style={{ fontSize: '0.55rem', color: '#555', marginBottom: 2 }}>E slow</div>
                  <MiniSparkline
                    data={energySlowHistRef.current}
                    max={Math.max(10, th!.slowStartEnergy * 4)}
                    color="#5ae"
                    thresholdY={th!.slowStartEnergy}
                    width={120}
                    height={24}
                  />
                </div>
              </div>
            </div>

            {/* Column 2: Omega + Spin + Freq */}
            <div style={{ flex: '1 1 180px', minWidth: 160 }}>
              <div style={{ color: '#555', fontSize: '0.6rem', fontWeight: 600, marginBottom: 6 }}>SIGNALS</div>
              <Meter
                label={'\u03c9 fast'}
                value={d.omegaFast}
                max={Math.max(40, th!.fastStartOmega * 2)}
                threshold={th!.fastStartOmega}
                color="#e85"
                unit=" rad/s"
              />
              <Meter
                label={'\u03c9 slow'}
                value={d.omegaSlow}
                max={Math.max(10, d.omegaSlow * 1.5 + 1)}
                color="#5ae"
                unit=" rad/s"
              />
              <Meter
                label="spin EMA"
                value={d.spinEma}
                max={127}
                threshold={th!.spinHighConfidence}
                color="#fa0"
              />
              <Meter
                label="osc freq"
                value={d.oscillationFreq}
                max={Math.max(5, d.oscillationFreq * 1.5 + 1)}
                threshold={th!.minOscFreq}
                color="#8d8"
                unit=" Hz"
              />
              <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                <div>
                  <div style={{ fontSize: '0.55rem', color: '#555', marginBottom: 2 }}>spin</div>
                  <MiniSparkline data={spinHistRef.current} max={127} color="#fa0" thresholdY={th!.spinHighConfidence} width={120} height={24} />
                </div>
                <div>
                  <div style={{ fontSize: '0.55rem', color: '#555', marginBottom: 2 }}>freq</div>
                  <MiniSparkline data={freqHistRef.current} max={6} color="#8d8" thresholdY={th!.minOscFreq} width={120} height={24} />
                </div>
              </div>
            </div>

            {/* Column 3: State machine progress */}
            <div style={{ flex: '1 1 150px', minWidth: 140 }}>
              <div style={{ color: '#555', fontSize: '0.6rem', fontWeight: 600, marginBottom: 6 }}>START PATHS</div>

              <div style={{ marginBottom: 6 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.6rem', color: '#666' }}>
                  <span>fast hold</span>
                  <span style={{ fontFamily: 'monospace' }}>{d.fastHoldMs.toFixed(0)} / {th!.fastStartHoldMs}ms</span>
                </div>
                <div style={{ height: 4, background: '#1a1a1a', borderRadius: 2, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${Math.min(100, fastProg * 100)}%`, background: fastProg >= 1 ? '#4a7' : '#e85', borderRadius: 2, transition: 'width 0.1s' }} />
                </div>
              </div>

              <div style={{ marginBottom: 6 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.6rem', color: '#666' }}>
                  <span>slow hold</span>
                  <span style={{ fontFamily: 'monospace' }}>{d.slowHoldMs.toFixed(0)} / {th!.slowStartHoldMs}ms</span>
                </div>
                <div style={{ height: 4, background: '#1a1a1a', borderRadius: 2, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${Math.min(100, slowProg * 100)}%`, background: slowProg >= 1 ? '#4a7' : '#5ae', borderRadius: 2, transition: 'width 0.1s' }} />
                </div>
              </div>

              <div style={{ marginBottom: 6 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.6rem', color: '#666' }}>
                  <span>slow angle</span>
                  <span style={{ fontFamily: 'monospace' }}>{d.slowHoldAngle.toFixed(2)} / {th!.slowMinAngle} rad</span>
                </div>
              </div>

              <div style={{ color: '#555', fontSize: '0.6rem', fontWeight: 600, marginBottom: 6, marginTop: 10 }}>END CONDITION</div>

              <div style={{ marginBottom: 6 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.6rem', color: '#666' }}>
                  <span>drop timer</span>
                  <span style={{ fontFamily: 'monospace' }}>{d.dropTimerMs.toFixed(0)} / {th!.endHoldMs}ms</span>
                </div>
                <div style={{ height: 4, background: '#1a1a1a', borderRadius: 2, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${Math.min(100, endProg * 100)}%`, background: endProg >= 1 ? '#a44' : '#553', borderRadius: 2, transition: 'width 0.1s' }} />
                </div>
              </div>

              <div style={{ fontSize: '0.6rem', color: '#666', fontFamily: 'monospace' }}>
                <div>E &lt; {th!.endEnergy.toFixed(1)}: {d.energySlow < th!.endEnergy ? '\u2713' : '\u2717'}</div>
                <div>{'\u03c9'} &lt; {th!.endOmega.toFixed(1)}: {d.omegaSlow < th!.endOmega ? '\u2713' : '\u2717'}</div>
                <div>spin &lt; {th!.endSpin.toFixed(1)}: {d.spinEma < th!.endSpin ? '\u2713' : '\u2717'}</div>
              </div>

              {d.impactGraceActive && (
                <div style={{ fontSize: '0.6rem', color: '#a74', fontFamily: 'monospace', marginTop: 4 }}>
                  grace: {d.impactGraceRemainMs.toFixed(0)}ms
                </div>
              )}

              <div style={{ fontSize: '0.6rem', color: '#666', fontFamily: 'monospace', marginTop: 4 }}>
                low-freq streak: {d.lowFreqCount}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
